import { useMemo } from 'react';
import { usePermissions } from './usePermissions';
import type { Icon } from '@phosphor-icons/react';

export interface NavItem {
  name: string;
  href: string;
  icon: Icon;
  /**
   * Other pages this one link stands for (the single "Rules" link). The link
   * opens the first of `href` + `alternates` the user can access — it is
   * dropped if none — and stays lit on all of them.
   */
  alternates?: string[];
}

/** Every path a link stands for: its href plus any alternates. */
export function navItemPaths(item: NavItem): string[] {
  return [item.href, ...(item.alternates ?? [])];
}

/** Keeps a leaf the user can reach, pointing it at the first page they can open. */
function resolveItem(
  item: NavItem,
  canAccess: (href: string) => boolean,
): NavItem | null {
  if (!item.alternates) return canAccess(item.href) ? item : null;
  const first = navItemPaths(item).find((href) => canAccess(href));
  return first ? { ...item, href: first } : null;
}

export interface NavGroup {
  name: string;
  /** Stable id for the group — a group is a container, it has no route. */
  key: string;
  icon: Icon;
  children: NavItem[];
  /**
   * Accessible name / flyout heading. Several sections each carry a group
   * displayed as "More", so the bare name is ambiguous on its own.
   */
  panelLabel?: string;
  /**
   * A "More" group is an overflow affordance, not a destination — it is styled
   * as a subdued secondary control so it doesn't compete with the real pages
   * in its section.
   */
  variant?: 'more';
}

export type NavEntry = NavItem | NavGroup;

export interface NavSection {
  label: string;
  entries: NavEntry[];
  /** Suppress the header — the label would be redundant (Overview, under the logo). */
  hideLabel?: boolean;
}

export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

/**
 * Filters navigation sections by the current user's warehouse-scoped
 * permissions. When the user switches warehouses, usePermissions returns new
 * warehouseRoles which causes canAccess to change, triggering useMemo to
 * recompute the filtered sections automatically.
 *
 * A leaf is kept only if its href (or, with `alternates`, any of its pages)
 * is accessible. A group's children are
 * filtered individually and the group is dropped once no accessible child
 * remains, so a submenu never renders empty — a PICKING_MANAGER still sees the
 * Settings group, holding only the Pick Split child. A section whose entries
 * are all filtered out is dropped too, so no header is left stranded.
 */
export function useFilteredNavigation(sections: NavSection[]): NavSection[] {
  const { canAccess } = usePermissions();

  return useMemo(() => {
    return sections
      .map((section) => ({
        ...section,
        entries: section.entries.flatMap<NavEntry>((entry) => {
          if (!isNavGroup(entry)) {
            const item = resolveItem(entry, canAccess);
            return item ? [item] : [];
          }
          const children = entry.children.flatMap((child) => {
            const item = resolveItem(child, canAccess);
            return item ? [item] : [];
          });
          return children.length > 0 ? [{ ...entry, children }] : [];
        }),
      }))
      .filter((section) => section.entries.length > 0);
  }, [sections, canAccess]);
}
